// Tratamento de erro padronizado por contexto (D-10).
// Transforma um erro de API (ApiError de lib/api.js, com .status e .payload) numa
// mensagem **amigável orientada ao usuário** + um **detalhe técnico** opcional para suporte.
// Nunca vaze "Erro 500" cru: use friendlyError/toastError em todo catch de chamada de API.
import { toast } from './toast.js';

// Mensagens por status HTTP. {contexto} é interpolado (ex.: "salvar conta").
const PorStatus = {
  0: 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.',
  400: 'Os dados enviados para {contexto} são inválidos. Revise os campos e tente de novo.',
  401: 'Sua sessão expirou. Faça login novamente para continuar.',
  403: 'Você não tem permissão para {contexto}.',
  404: 'Não encontramos o registro para {contexto}. Ele pode ter sido removido.',
  405: 'Esta operação não está disponível para {contexto} no momento. Avise o suporte.',
  409: 'Há um conflito ao {contexto} (registro duplicado ou já alterado). Recarregue e tente de novo.',
  422: 'Alguns campos de {contexto} precisam de atenção antes de salvar.',
  429: 'Muitas tentativas em pouco tempo. Aguarde alguns instantes e tente novamente.',
  500: 'Tivemos um problema interno ao {contexto}. Tente novamente; se persistir, avise o suporte.',
  502: 'O servidor está indisponível no momento. Tente novamente em instantes.',
  503: 'O serviço está temporariamente indisponível. Tente novamente em instantes.',
  504: 'O servidor demorou para responder ao {contexto}. Tente novamente.',
};

function interp(template, contexto) {
  return template.replace('{contexto}', contexto || 'concluir a ação');
}

/**
 * friendlyError(err, contexto) -> { titulo, mensagem, tecnico, status }
 * - contexto: rótulo curto da ação ("salvar conta", "excluir cartão").
 * - mensagem: frase amigável para o usuário.
 * - tecnico: mensagem real do backend (para o suporte); pode ser '' .
 */
export function friendlyError(err, contexto) {
  const status = err?.status ?? 0;
  // Mensagem técnica do backend (payload.message) ou a própria message do erro.
  const tecnico = err?.payload?.message || (status ? err?.message : '') || '';

  // Erro de validação do backend (422/400) costuma trazer uma mensagem útil — priorize-a.
  const base = PorStatus[status];
  let mensagem;
  if ((status === 422 || status === 400) && err?.payload?.message) {
    mensagem = err.payload.message;
  } else if (base) {
    mensagem = interp(base, contexto);
  } else if (err?.message && status === 0) {
    mensagem = PorStatus[0];
  } else {
    mensagem = interp('Não foi possível {contexto}. Tente novamente.', contexto);
  }

  return {
    titulo: status === 401 ? 'Sessão expirada' : `Erro ao ${contexto || 'processar'}`,
    mensagem,
    tecnico: tecnico && tecnico !== mensagem ? tecnico : '',
    status,
  };
}

/**
 * toastError(err, contexto) — atalho que mostra a frase amigável num toast de erro.
 * Anexa o detalhe técnico entre parênteses quando houver, para facilitar o suporte.
 */
export function toastError(err, contexto) {
  const { mensagem, tecnico } = friendlyError(err, contexto);
  const texto = tecnico ? `${mensagem} (${tecnico})` : mensagem;
  toast(texto, 'error');
  return texto;
}
